import React, { useEffect, useState, useRef } from 'react';
import styles from './CommentsSidebar.module.scss';
import Loading from '../Loading';
import api from '../../../services/api';
import * as CommonIcons from '../../CommonIcons';
import { dateFormatterShortMonth, highchartsLabelFormatterWithoutMsFromMs } from '../../../utils/dateFormatter';
import { useTranslation } from 'react-i18next';

export default function CommentsSidebar({ show, onClose, baseUrl, children, timeViewMode, onCommentClickCallback, position }) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState(null);
  const [error, setError] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(undefined);

  // Audio playback state
  const [playingId, setPlayingId] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (show && baseUrl) {
      setLoading(true);
      setComments(null);
      setError(null);
      api
        .get(`${baseUrl}/list?timeDisplayType=${timeViewMode}`)
        .then((response) => {
          setComments(response.data.comments);
          setLoading(false);
        }, (error) => {
          setError(t('recordView.comments.failedToLoad'));
          setLoading(false);
        });
    }
    // Cleanup audio when sidebar closes
    return () => {
      stopAudio();
    };
    // eslint-disable-next-line
  }, [show, baseUrl, timeViewMode]);

  useEffect(() => {
    if (show && position?.width) {
      setSidebarWidth(position.width);
    }
  }, [show, position?.width]);

  // Cleanup audio URL when component unmounts or audio changes
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const playAudio = async (commentUid) => {
    stopAudio();
    try {
      const response = await api.get(`${baseUrl}/${commentUid}/play`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setAudioUrl(url);
      setPlayingId(commentUid);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play();
        }
      }, 0);
    } catch {
      setError(t('recordView.comments.failedToLoadAudio'));
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setPlayingId(null);
  };

  const handleAudioEnded = () => {
    setPlayingId(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  };

  // Calculate adjusted left to prevent overflow
  let adjustedLeft = position?.left || 0;
  const maxWidth = 512; // 32rem in px
  if (typeof window !== 'undefined' && position?.left !== undefined) {
    const rightEdge = adjustedLeft + maxWidth;
    const viewportWidth = window.innerWidth;
    if (rightEdge > viewportWidth - 8) { // 8px margin
      adjustedLeft = Math.max(8, viewportWidth - maxWidth - 8);
    }
  }

  if (!show) return null;
  return (
    <div className={styles.sidebarOverlay} style={{ pointerEvents: 'none' }}>
      <div
        className={styles.sidebarBox}
        style={{
          position: 'absolute',
          top: position?.top || 0,
          left: adjustedLeft,
          width: sidebarWidth ? `${sidebarWidth}px` : undefined,
          pointerEvents: 'auto',
        }}
      >
        <div className={styles.sidebarHeader}>
          <span className={styles.headerTitle}>{t('recordView.comments.header')}</span>
          <button className={styles.closeBtn} onClick={onClose} aria-label={t('common.closeButton')}>&times;</button>
        </div>
        <div className={styles.sidebarContent}>
          {loading && <Loading />}
          {error && <div style={{ color: 'red' }}>{error}</div>}
          {!loading && !error && (
            Array.isArray(comments) && comments.length > 0 ? (
              <ul className={styles.commentsList}>
                {comments.map((comment) => (
                  <li key={comment.uid} className={styles.commentRow}>
                    <span
                      className={styles.commentTimestamp}
                      onClick={() => onCommentClickCallback && onCommentClickCallback(comment.timeStampMs)}
                      tabIndex={onCommentClickCallback ? 0 : undefined}
                      role={onCommentClickCallback ? "button" : undefined}
                    >
                      {timeViewMode === 1
                        ? dateFormatterShortMonth(comment.timeStampMs)
                        : highchartsLabelFormatterWithoutMsFromMs(comment.timeStampMs, true)}
                    </span>
                    <span className={styles.commentText}>
                      {comment.commentType === 10 && comment.text}
                      {comment.commentType === 20 && (
                        <>
                          {playingId === comment.uid ? (
                            <>
                              <button
                                type="button"
                                className={styles.audioControlBtn}
                                onClick={stopAudio}
                              >
                                <CommonIcons.AudioStopButtonRed />
                              </button>
                              {audioUrl && (
                                <audio
                                  ref={audioRef}
                                  src={audioUrl}
                                  onEnded={handleAudioEnded}
                                  style={{ display: 'none' }}
                                />
                              )}
                            </>
                          ) : (
                            <button
                              type="button"
                              className={styles.audioControlBtn}
                              onClick={() => playAudio(comment.uid)}
                            >
                              <CommonIcons.AudioPlayButtonRed />
                              </button>                            
                          )}
                          <span className={styles.recognized}>[{comment.text}]</span>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
                children || <div>{t('recordView.comments.noComments')}</div>
            )
          )}
        </div>
      </div>
    </div>
  );
}